import { useCallback, useState } from 'react'

import type { TableDensity, TableState } from '../DataTable.types'

// Hook for managing table state
export const useTableState = (initialState?: Partial<TableState>) => {
  const [state, setState] = useState<TableState>({
    globalFilter: '',
    density: 'normal',
    showFilters: false,
    showColumnVisibility: false,
    exportFormat: undefined,
    ...initialState
  })

  // Update specific state properties
  const updateState = useCallback((updates: Partial<TableState>) => {
    setState((prev) => ({ ...prev, ...updates }))
  }, [])

  // Specific setters for common operations
  const setGlobalFilter = useCallback(
    (filter: string) => {
      updateState({ globalFilter: filter })
    },
    [updateState]
  )

  const setDensity = useCallback(
    (density: TableDensity) => {
      updateState({ density })
    },
    [updateState]
  )

  const toggleFilters = useCallback(() => {
    setState((prev) => ({ ...prev, showFilters: !prev.showFilters }))
  }, [])

  const toggleColumnVisibility = useCallback(() => {
    setState((prev) => ({ ...prev, showColumnVisibility: !prev.showColumnVisibility }))
  }, [])

  const setExportFormat = useCallback(
    (format: 'csv' | 'excel' | 'pdf' | undefined) => {
      updateState({ exportFormat: format })
    },
    [updateState]
  )

  // Reset state to defaults
  const resetState = useCallback(() => {
    setState({
      globalFilter: '',
      density: 'normal',
      showFilters: false,
      showColumnVisibility: false,
      exportFormat: undefined,
      ...initialState
    })
  }, [initialState])

  return {
    state,
    setState,
    updateState,
    setGlobalFilter,
    setDensity,
    toggleFilters,
    toggleColumnVisibility,
    setExportFormat,
    resetState
  }
}

// Hook for managing table preferences with localStorage persistence
export const useTablePreferences = (tableId: string, initialState?: Partial<TableState>) => {
  const storageKey = `table-preferences-${tableId}`

  // Load initial state from localStorage
  const loadStoredState = useCallback((): Partial<TableState> => {
    try {
      const stored = localStorage.getItem(storageKey)
      return stored ? JSON.parse(stored) : {}
    } catch {
      return {}
    }
  }, [storageKey])

  const [state, setState] = useState<TableState>(() => ({
    globalFilter: '',
    density: 'normal',
    showFilters: false,
    showColumnVisibility: false,
    exportFormat: undefined,
    ...initialState,
    ...loadStoredState()
  }))

  // Save state to localStorage
  const saveState = useCallback(
    (newState: TableState) => {
      try {
        localStorage.setItem(storageKey, JSON.stringify(newState))
      } catch {
        // Ignore localStorage errors
      }
    },
    [storageKey]
  )

  // Update state with persistence
  const updateState = useCallback(
    (updates: Partial<TableState>) => {
      setState((prev) => {
        const newState = { ...prev, ...updates }
        saveState(newState)
        return newState
      })
    },
    [saveState]
  )

  // Clear stored preferences
  const clearPreferences = useCallback(() => {
    try {
      localStorage.removeItem(storageKey)
    } catch {
      // Ignore localStorage errors
    }
    setState({
      globalFilter: '',
      density: 'normal',
      showFilters: false,
      showColumnVisibility: false,
      exportFormat: undefined,
      ...initialState
    })
  }, [storageKey, initialState])

  return {
    state,
    updateState,
    clearPreferences,
    setGlobalFilter: (filter: string) => updateState({ globalFilter: filter }),
    setDensity: (density: TableDensity) => updateState({ density }),
    toggleFilters: () => updateState({ showFilters: !state.showFilters }),
    toggleColumnVisibility: () =>
      updateState({ showColumnVisibility: !state.showColumnVisibility }),
    setExportFormat: (format: 'csv' | 'excel' | 'pdf' | undefined) =>
      updateState({ exportFormat: format })
  }
}
